import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { createTeacherAbsence } from '../../api/ticketingApi';
import { useTranslation } from 'react-i18next';

type Props = {
  reservaId: number;
  buttonText?: string;
  onSuccess?: (ticketId: string) => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'inherit' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium' | 'large';
};

export default function TeacherAbsenceButton({
  reservaId,
  buttonText,
  onSuccess,
  variant = 'outlined',
  color = 'error',
  size = 'small',
}: Props) {
  const { t } = useTranslation<'translation'>();
  const defaultButtonText = t('tickets.teacherAbsence.button');
  const [open, setOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  }>({ open: false, msg: '', sev: 'success' });

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await createTeacherAbsence(reservaId, comment, files);
      const ticketId = res.id || res.ticketId || '';
      setSnack({
        open: true,
        msg: res.dedup
          ? t('tickets.teacherAbsence.success.adjuntado', { ticketId })
          : t('tickets.teacherAbsence.success.creado', { ticketId }),
        sev: 'success',
      });
      onSuccess?.(ticketId);
      setOpen(false);
      setComment('');
      setFiles([]);
    } catch (e: any) {
      setSnack({
        open: true,
        msg: e?.response?.data?.message || t('tickets.teacherAbsence.error.crearError'),
        sev: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<PersonOffIcon />}
        variant={variant}
        color={color}
        size={size}
        onClick={() => setOpen(true)}
      >
        {buttonText || defaultButtonText}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tickets.teacherAbsence.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('tickets.teacherAbsence.field.comentario')}
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />
            <Button component="label" variant="text">
              {t('tickets.common.adjuntarArchivos')}
              <input hidden multiple type="file" onChange={onChooseFiles} />
            </Button>
            {files.length > 0 && (
              <small>{t('tickets.hrInquiry.archivosSeleccionados', { count: files.length })}</small>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            {t('tickets.common.cancelar')}
          </Button>
          <Button onClick={submit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : t('tickets.common.crearTicket')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
